<template>
	<div id="selectProfile" v-if="profiles.length > 0">
		<v-data-table
			:headers="headers"
			:items="profiles"
			:items-per-page="10"
			:hide-default-footer="profiles.length <= 10"
			mobile-breakpoint="500"
			item-key="profileId"
			class="select-profile-data-table"
		>
			<template v-slot:item.profileLabel="{ item }">
				<div class="select-profile-text">
					<template v-if="activeTenantId === 'master'">
						<template v-if="item.isActive === true">
							<v-icon class="active-profile" title="Active">mdi-account-group</v-icon>
						</template>
						<template v-else>
							<v-icon class="inactive-profile" title="Inactive">mdi-eye-off-outline</v-icon>
						</template>
					</template>
					<template v-if="item.isLive && activeTenantId !== 'master'">
						<v-chip pill class="select-profile-live-current">LIVE</v-chip>
					</template>
					<template v-else-if="activeTenantId === 'master' && item.masterDefault === true">
						<v-chip pill class="select-profile-live-current">Default</v-chip>
					</template>
					<div class="select-profile-name">
						{{ item.profileLabel }}
					</div>
					<div class="select-profile-id">
						{{ item.profileId }}
					</div>
				</div>
			</template>
			<template v-slot:item.re-configure="{ item }">
				<div class="select-profile-actions">

					<template v-if="item.isModifiable === false">
						<v-btn text class="select-profile-action-primary"
							@click="setNewName(item, 'C')"
						>
							Create a Copy
						</v-btn>
						<v-icon class="select-profile-locked"
							title="Locked"
							label="Locked"
						>
							mdi-lock
						</v-icon>
					</template>
				
					<template v-else>
						<v-btn class="secondary select-profile-action-primary"
							@click="onReconfigure(item)"
						>
							Re-Configure
						</v-btn>

						<v-menu
							v-model="item.more"
							:close-on-content-click="true"
							offset-y
							left
							transition="slide-y-transition"
							class="more-options"
						>
							<template v-slot:activator="{ on, attrs }">
								<v-btn
									text
									v-bind="attrs"
									v-on="on"
									aria-label="menu"
									title="menu"
									class="more-options-icon"
								>
									<v-icon>mdi-dots-vertical</v-icon>
								</v-btn>
							</template>
							<div class="more-options-activator">
								<v-btn
									class="more-options-rename"
									@click="setNewName(item, 'R')"
								>
									Rename
								</v-btn>
							</div>
							<div class="more-options-activator">
								<v-btn
									class="more-options-copy"
									@click="setNewName(item, 'C')"
								>
									Create a Copy
								</v-btn>
							</div>
							<div v-if="activeTenantId === 'master'" class="more-options-activator">
								<v-btn
									class="more-options-activate"
									@click="activateProfile(item)"
								>
									<template v-if="item.isActive">
										Inactivate
									</template>
									<template v-else>
										Activate
									</template>
								</v-btn>
							</div>
							<div class="more-options-activator">
								<v-btn
									class="more-options-delete dangerous"
									@click="deleteProfile(item)"
								>
									Delete
								</v-btn>
							</div>
						</v-menu>
					</template>
				</div>
			</template>
		</v-data-table>

		<!-- RENAME Modal -->
		<modal-dialog
			:title="
				renameAction === 'rename' ? 'Rename Profile' : 'Create a Copy'
			"
			:useActivator="false"
			:triggerCounter="showRenameDialogCount"
			:executeText="
				renameAction === 'rename'
					? 'Rename &amp; Save the Profile'
					: 'Copy &amp; Create the Profile'
			"
			:executeAction="
				renameAction === 'rename' ? onRenameProfile : onConfigureCopy
			"
		>
			<div slot="content" v-if="profileToEdit">
				<template v-if="renameAction === 'rename'">
					<p>Renaming {{ profileToEdit.profileLabel }}</p>
					<v-text-field
						label="Rename profile as:"
						v-model="newName"
						outlined
					/>
				</template>
				<template v-else>
					<p>Creating a copy of {{ profileToEdit.profileLabel }}</p>
					<v-text-field
						label="New profile name:"
						v-model="newName"
						outlined
					/>
				</template>
			</div>
		</modal-dialog>

		<!-- DELETE Modal -->
		<modal-dialog
			title="Are you sure?"
			:useActivator="false"
			:triggerCounter="showDeleteDialogCount"
			executeText="Delete"
			:executeAction="onDeleteProfile"
		>
			<div slot="content" v-if="profileToEdit">
				<p>
					Are you sure you want to delete
					{{ profileToEdit.profileLabel }}?
				</p>
			</div>
		</modal-dialog>
	</div>
	<div v-else>
		<v-skeleton-loader type="table"></v-skeleton-loader>
	</div>
</template>

<script>
	/**
	 * @fileoverview select-profile.vue is the first step of four in the Configuration Explorer. It provides a list of profiles
	 * available for the active tenant. It relies on vuex to map the state variables from the store files.
	 * 
	 * @author Sophia Frankel <sophia.frankel@quavo.com>
	 */
	import { mapState } from "vuex";

	export default {
		name: "selectProfile",
		data() {
			return {
				newName: "",	// name for a renamed or copied profile
				showRenameDialogCount: 0,	// rename dialog activator
				showDeleteDialogCount: 0,	// delete dialog activator
				profileToEdit: null,	// profile being edited
				renameAction: "copy",	// action for renaming a profile: copy or rename
				/**
				 * Headers for available profiles data table
				 */
				headers: [
					{
						text: "Profile",
						align: "start",
						value: "profileLabel",
						class: "select-profile-label"
					},
					{
						text: "",
						value: "re-configure",
						align: "center",
						sortable: false,
						class: "select-profile-action"
					},
				],
			};
		},
		computed: {
			...mapState({
				activeTenantId: (state) => state.account.activeTenantId,
				profiles: (state) => state.config.profileList,
			}),
		},
		methods: {
			/**
			 * Create a copy of profileToEdit
			 */
			onConfigureCopy() {
				var payload = {
					profileId: this.profileToEdit.profileId,
					tenantId: this.activeTenantId,
					name: this.newName,
					activeTenantId: this.activeTenantId
				};

				if (this.profileToEdit.TenantId === 'master'){
					payload.tenantId = 'master';
				}

				this.$store.dispatch("configureCopyProfile", payload);
				this.newName = "";
			},
			/**
			 * Emit reconfigure was selected for the given profile
			 * @param {Object} profile - selected profile
			 */
			onReconfigure(profile) {
				this.$emit("onReconfigure", profile);
			},
			/**
			 * Display the delete dialog box
			 * @param {Object} profile - selected profile
			 */
			deleteProfile(profile) {
				this.profileToEdit = profile;
				this.showDeleteDialogCount += 1;
			},
			/**
			 * Delete profileToEdit
			 */
			onDeleteProfile() {
				var payload = {
					profileId: this.profileToEdit.profileId,
					tenantId: this.activeTenantId,
				};
				this.$store.dispatch("deleteProfile", payload);
			},
			/**
			 * Create a placeholder name for renaming or copying a profile
			 * Open the show rename dialog box
			 * @param {Object} profile - profile being edited
			 * @param {String} code -  renaming or copying character code
			 */
			setNewName(profile, code) {
				this.profileToEdit = profile;

				var name = "";
				switch (code) {
					// rename placeholder
					case "R":
						this.renameAction = "rename";
						name = profile.profileLabel;
						break;
					// copy placeholder
					case "C":
						this.renameAction = "copy";
						name = profile.profileLabel + " " + Date.now();
						break;
					default:
						break;
				}

				this.newName = name;
				this.showRenameDialogCount += 1;
			},
			/**
			 * Rename the profileToEdit
			 */
			onRenameProfile() {
				var payload = {
					profileId: this.profileToEdit.profileId,
					tenantId: this.activeTenantId,
					name: this.newName,
				};

				this.$store.dispatch("renameProfile", payload);
				this.newName = "";
			},
			/**
			 * Make selected profile active
			 * @param {Object} profile - selected profile
			 */
			activateProfile(profile) {
				profile.isActive = profile.isActive ? false : true;

				let payload = {
					tenantId: this.activeTenantId,
					profileId: profile.profileId,
					isActive: profile.isActive
				};

				this.$store.dispatch("activateProfile", payload);
			}
		},
	};
</script>

<!-- Styles defined in _selectProfile.scss -->